/**
 * Affiliate storage utilities for managing affiliate IDs with expiry
 */

import { envConfig } from "../constants";

const AFFILIATE_KEY = "writevoice_affiliate";
const EXPIRY_DAYS = 60;

interface AffiliateData {
  affiliateId: string;
  expiresAt: number; // Unix timestamp
}

/**
 * Store affiliate ID with 30-day expiry
 */
export function setAffiliate(affiliateId: string): void {
  const expiresAt = Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  const data: AffiliateData = {
    affiliateId,
    expiresAt,
  };
  localStorage.setItem(AFFILIATE_KEY, JSON.stringify(data));
}

/**
 * Get affiliate ID if not expired
 * Returns null if no affiliate or if expired
 */
export function getAffiliate(): string | null {
  try {
    const stored = localStorage.getItem(AFFILIATE_KEY);
    if (!stored) return null;

    const data: AffiliateData = JSON.parse(stored);

    // Check if expired
    if (Date.now() > data.expiresAt) {
      clearAffiliate();
      return null;
    }

    return data.affiliateId;
  } catch (error) {
    console.error("[AFFILIATE] Error reading affiliate:", error);
    return null;
  }
}

/**
 * Clear affiliate from storage
 */
export function clearAffiliate(): void {
  localStorage.removeItem(AFFILIATE_KEY);
}

/**
 * Check affiliate from backend and store locally
 * Returns the affiliate ID if found
 */
export async function checkAndStoreAffiliate(): Promise<string | null> {
  try {
    // First check if we already have a valid affiliate stored
    const existing = getAffiliate();
    if (existing) {
      return existing;
    }

    // Check backend for IP-based affiliate
    const response = await fetch(
      `${envConfig.EXPRESS_BACKEND_URL}/api/affiliate/check`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      console.error(
        "[AFFILIATE] Failed to check affiliate:",
        response.statusText
      );
      return null;
    }

    const data = await response.json();

    if (data.affiliateId) {
      setAffiliate(data.affiliateId);
      return data.affiliateId;
    }

    return null;
  } catch (error) {
    console.error("[AFFILIATE] Error checking affiliate:", error);
    return null;
  }
}
